"use client";

import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Star, Ship, Anchor } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface TestimonialProps {
  name: string;
  role: string;
  company?: string;
  testimonial: string;
  rating: number;
  image?: string;
}

const testimonials: TestimonialProps[] = [
  {
    name: "Captain Mike Johnson",
    role: "Yacht Owner",
    testimonial: "FuelOx Marine has been a game-changer for my yacht. I've noticed significantly improved fuel efficiency and reduced maintenance costs since I started using it. The engine runs smoother and cleaner than ever before.",
    rating: 5,
    image: "https://i.pravatar.cc/150?u=mike",
  },
  {
    name: "Sarah Williams",
    role: "Marina Manager",
    company: "Oceanside Marina",
    testimonial: "We've been recommending FuelOx Marine to all our clients. The feedback has been overwhelmingly positive with reports of better performance and less carbon buildup. It's become a staple product at our marina.",
    rating: 5,
    image: "https://i.pravatar.cc/150?u=sarah",
  },
  {
    name: "Robert Chen",
    role: "Commercial Fishing Operator",
    testimonial: "In the commercial fishing business, fuel costs are a major expense. Since implementing FuelOx Marine across our fleet, we've seen a 15% reduction in fuel consumption. The ROI has been remarkable.",
    rating: 4,
    image: "https://i.pravatar.cc/150?u=robert",
  },
  {
    name: "Lisa Thompson",
    role: "Boat Mechanic",
    company: "Thompson Marine Services",
    testimonial: "As a marine mechanic, I've seen firsthand the difference FuelOx makes. Engines treated with this product have noticeably less carbon buildup and run more efficiently. I recommend it to all my customers.",
    rating: 5,
    image: "https://i.pravatar.cc/150?u=lisa",
  },
  {
    name: "James Wilson",
    role: "Charter Boat Captain",
    company: "Blue Water Charters",
    testimonial: "Running daily charters means my engines get heavy use. FuelOx Marine has extended the life of my engines and improved their performance. It's worth every penny for the peace of mind alone.",
    rating: 5,
    image: "https://i.pravatar.cc/150?u=james",
  },
  {
    name: "Emily Rodriguez",
    role: "Sailing Enthusiast",
    testimonial: "Even for my sailboat's auxiliary engine, FuelOx Marine has made a noticeable difference. The engine starts more reliably and runs cleaner. I won't go without it now.",
    rating: 4,
    image: "https://i.pravatar.cc/150?u=emily",
  },
];

function TestimonialCard({ name, role, company, testimonial, rating, image }: TestimonialProps) {
  return (
    <div className="bg-muted rounded-md h-full p-6 flex justify-between flex-col">
      <div className="flex justify-between items-start mb-4">
        <div className="flex gap-1">
          {Array.from({ length: 5 }).map((_, index) => (
            <Star
              key={index}
              size={16}
              className={cn(
                index < rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "fill-muted-foreground/20 text-muted-foreground/20"
              )}
            />
          ))}
        </div>
        <Ship className="w-8 h-8 stroke-1 text-primary/70" />
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <h3 className="text-xl tracking-tight font-medium">{testimonial.length > 100 ? `${testimonial.substring(0, 100)}...` : testimonial}</h3>
          <p className="text-muted-foreground mt-2">{testimonial}</p>
        </div>
        <div className="flex flex-row gap-2 text-sm items-center mt-auto pt-4 border-t border-border">
          <Avatar className="h-10 w-10">
            <AvatarImage src={image} alt={`Profile picture of ${name}, ${role}${company ? ", " + company : ""}`} />
            <AvatarFallback>{name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{name}</span>
            <span className="text-muted-foreground text-xs">
              {role}{company ? ` @ ${company}` : ""}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarineTestimonials() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div id="testimonials" className="w-full py-20 bg-background">
      <div className="container mx-auto">
        <div className="flex flex-col gap-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="flex justify-center mb-4">
              <Anchor className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Trusted by Marine Professionals Worldwide
            </h2>
            <p className="text-muted-foreground text-lg">
              Hear what boat owners, captains, and marine mechanics have to say about how FuelOx Marine has improved their vessels' performance.
            </p>
          </div>
          
          <Carousel setApi={setApi} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, index) => (
                <CarouselItem className="md:basis-1/2 lg:basis-1/3 p-2" key={index}>
                  <TestimonialCard {...testimonial} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          
          <div className="flex justify-center gap-1 mt-4">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`h-2 w-2 rounded-full transition-colors ${
                  index === current ? "bg-primary" : "bg-muted-foreground/20"
                }`}
                onClick={() => api?.scrollTo(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-muted-foreground">
              Join thousands of satisfied customers who have improved their marine engine performance with FuelOx Marine.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsDemo() {
  return <MarineTestimonials />;
}
